import { addButtonStyles } from '@components/Customization/PoiDesigner/designer.styles';
import { DragOverlay, useDroppable } from '@dnd-kit/core';
import {
  blockStep, locateBlock, sameTarget, type DraftIssue, type DraftTarget, type PageSection, type ReportDesignDraft,
} from '@lib/customization/reportDesign';
import { useLocalization } from '@lib/localization';
import { Box, Button, GlobalStyles, Stack, Typography } from '@mui/material';
import { reportDesignLimits, type ReportBlock } from '@store/api/reportDesignEndpoints';
import { useEffect, useRef } from 'react';
import { PlusIcon } from 'react-line-awesome';
import { pageLabel } from '../describe';
import { type DesignImages } from '../ImageField';
import { type BlockFrameActions } from './BlockFrame';
import { PaletteGhost } from './BlockPalette';
import BlockView from './BlockView';
import {
  addPageStyles, canvasDeskStyles, canvasFrameStyles, carryingStyles, ghostStyles, navyGhostStyles, overlayStyle,
  sheetColumnStyles,
} from './DesignCanvas.styles';
import { dragIds, type DragSource, type Placement } from './dragging';
import PageSheet from './PageSheet';
import { hasResultsPage, type Print } from './print';
import { BLOCK_WIDTH } from './print.styles';
import ResultsSheet from './ResultsSheet';
import { usePointScale } from './usePointScale';
import { usePrint } from './usePrint';

/** Everything the canvas can ask of the designer. */
export interface CanvasActions {
  select: (target: DraftTarget) => void;
  addPage: (section: PageSection) => void;
  /** Moves a page within its section. */
  movePage: (pageId: string, to: number) => void;
  removePage: (pageId: string) => void;
  /**
   * Moves a block one place up or down, from the edge of its page onto the next one that has room.
   * Like the two below it is a press in the block's toolbar, which takes the pressed button
   * away: the designer puts the keyboard's focus back on the canvas after each (`useHandover`).
   * It also points at the block, so that the canvas follows it where the step takes it out of view.
   */
  stepBlock: (blockId: string, by: -1 | 1) => void;
  duplicateBlock: (blockId: string) => void;
  removeBlock: (blockId: string) => void;
}

/** A part of the design the canvas is asked to scroll to. `turn` rises with each asking. */
export interface Shown {
  target: DraftTarget;
  turn: number;
}

interface Props {
  draft: ReportDesignDraft;
  selection: DraftTarget;
  canEdit: boolean;
  /** What stands in the way of a save: whatever one is about is marked on its sheet. */
  issues: DraftIssue[];
  images: DesignImages;
  /** What is being dragged, and where it would land (`useBlockDrag`). */
  carried: DragSource | null;
  placement: Placement | null;
  /** What was last pointed at: from the sections list or an issue, or a block that a step in its toolbar moved. */
  shown: Shown | null;
  actions: CanvasActions;
}

/** The block that is being dragged, as the overlay shows it: itself, at the size it has on its sheet. */
const BlockGhost: React.FC<{ block: ReportBlock; dark: boolean; print: Print }> = ({ block, dark, print }) => {
  const scale = usePointScale(BLOCK_WIDTH);

  return (
    <Box ref={scale.ref} style={scale.style} sx={dark ? [ghostStyles, navyGhostStyles] : ghostStyles}>
      <BlockView block={block} print={print} dark={dark} />
    </Box>
  );
};

/**
 * The design as pages: a column of sheets in the order the report prints them. Every cover,
 * then one sheet for the results, then every closing page, each at A4's proportions and at
 * the width the column gives it. Where the report has no page of results, because the design
 * prints neither a summary nor checkpoints, their place in the column is not drawn as a
 * sheet and no page number counts it (`hasResultsPage`).
 *
 * A cover or a closing page is composed here: its blocks are drawn as the report prints
 * them, selected by a click, and dragged within their page, to another one or in from the
 * palette. The summary and the checkpoints are templates: their sheet shows what the chosen
 * one prints, and a click on a part of it opens that part's settings.
 *
 * It is an approximation in HTML of what the server draws, over a copy of the server's
 * sample inspection and in the interface's language. Close enough to compose on; the PDF tab
 * beside it is the report itself.
 *
 * A reader who may not edit gets the same sheets with nothing to pick up: a click still
 * selects, and the inspector shows the settings, disabled.
 */
const DesignCanvas: React.FC<Props> = ({ draft, selection, canEdit, issues, images, carried, placement, shown, actions }) => {
  const { t } = useLocalization();
  const print = usePrint(draft.theme, images);
  // What a drag is over while it is on no page, so that the canvas scrolls under it there too (`collide`).
  const desk = useDroppable({ id: dragIds.desk, disabled: !canEdit });

  const isSelected = (target: DraftTarget) => sameTarget(selection, target);
  const isFlagged = (target: DraftTarget) => issues.some((issue) => sameTarget(issue.target, target));

  // A pointing is answered once, by the first part that stands for its target: as a rule in
  // the render it arrives in, and for a header or a footer that is switched off in the one
  // that draws its strip. After that it is spent and no part is handed its turn. Otherwise a
  // part that came to stand for the same target later would take the canvas to itself with
  // nothing pointing at it: the strips of the next closing page when the one that showed
  // them turns navy, the frame of a block that is dropped on another page.
  const answered = useRef(0);
  const pointed = shown && shown.turn !== answered.current ? shown : null;
  // Whether a part was handed the turn while this render was drawn: the sheets ask for a
  // target's turn only where they draw a part for it.
  let taken = false;
  const turnOf = (target: DraftTarget) => {
    if (!pointed || !sameTarget(pointed.target, target)) return undefined;

    taken = true;

    return pointed.turn;
  };

  useEffect(() => {
    if (pointed && taken) answered.current = pointed.turn;
  });

  // The pages of the report that have a sheet here: its covers, the first page of its results
  // where it prints one, its closing pages. Results that run on to more pages are not counted.
  const results = hasResultsPage(draft) ? 1 : 0;
  const pageCount = draft.covers.length + results + draft.closings.length;
  // Pointed at, the header or the footer is brought into view on the results sheet. Where the
  // report has no page of results, on the first closing page that prints the two.
  const stripsOn = results ? undefined : draft.closings.find((page) => page.background !== 'navy')?.id;

  const blockActions = (block: ReportBlock): BlockFrameActions => {
    const full = (locateBlock(draft, block.id)?.page.blocks.length ?? 0) >= reportDesignLimits.blocksPerPage;

    return {
      select: () => actions.select({ kind: 'block', id: block.id }),
      stepUp: blockStep(draft, block.id, -1) ? () => actions.stepBlock(block.id, -1) : undefined,
      stepDown: blockStep(draft, block.id, 1) ? () => actions.stepBlock(block.id, 1) : undefined,
      duplicate: full ? undefined : () => actions.duplicateBlock(block.id),
      remove: () => actions.removeBlock(block.id),
    };
  };

  const sheets = (section: PageSection) => draft[section].map((page, index) => (
    <PageSheet
      key={page.id}
      page={page}
      section={section}
      index={index}
      count={draft[section].length}
      label={pageLabel(draft, page.id, t)}
      // A cover never takes the header and the footer.
      chrome={section === 'closings'
        ? {
          header: draft.header,
          footer: draft.footer,
          page: { number: draft.covers.length + results + 1 + index, of: pageCount },
          shown: page.id === stripsOn,
        }
        : undefined}
      print={print}
      canEdit={canEdit}
      isSelected={isSelected}
      isFlagged={isFlagged}
      shown={turnOf}
      placement={placement}
      actions={{ select: actions.select, movePage: actions.movePage, removePage: actions.removePage, blockActions }}
    />
  ));

  /** After a section's last sheet, and in the section's place while it has none: where its next page comes from. */
  const pageSlot = (section: PageSection) => {
    const none = draft[section].length === 0;

    // A reader is told that a section is empty, and not offered a page they cannot add.
    if (!canEdit && !none) return null;

    const emptyKey = section === 'covers' ? 'ReportDesign_NoCovers' : 'ReportDesign_NoClosings';
    const titleKey = section === 'covers' ? 'ReportDesign_Section_Covers' : 'ReportDesign_Section_Closings';

    return (
      <Stack direction="row" sx={addPageStyles}>
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{t(none ? emptyKey : titleKey)}</Typography>
        {canEdit && (
          <Button
            size="small"
            startIcon={<PlusIcon />}
            sx={addButtonStyles}
            disabled={draft[section].length >= reportDesignLimits.pages}
            onClick={() => actions.addPage(section)}
          >
            {t('ReportDesign_Page_Add')}
          </Button>
        )}
      </Stack>
    );
  };

  const lifted = carried?.kind === 'block' ? locateBlock(draft, carried.blockId) : null;

  return (
    <Box sx={canvasFrameStyles}>
      <Box sx={canvasDeskStyles}>
        <Box ref={desk.setNodeRef} sx={sheetColumnStyles}>
          {sheets('covers')}
          {pageSlot('covers')}

          <ResultsSheet
            draft={draft}
            page={results ? { number: draft.covers.length + 1, of: pageCount } : undefined}
            print={print}
            isSelected={isSelected}
            isFlagged={isFlagged}
            shown={turnOf}
            onSelect={actions.select}
          />

          {sheets('closings')}
          {pageSlot('closings')}
        </Box>
      </Box>

      {carried && <GlobalStyles styles={carryingStyles} />}

      {/*
        No drop animation: it flies the overlay back to where the block was picked up, and
        the block is put in its new place only by the drop itself.
      */}
      <DragOverlay dropAnimation={null} style={overlayStyle}>
        {carried?.kind === 'palette' && <PaletteGhost type={carried.type} />}
        {lifted && <BlockGhost block={lifted.block} dark={lifted.page.background === 'navy'} print={print} />}
      </DragOverlay>
    </Box>
  );
};

export default DesignCanvas;
